import React from 'react';
import { ColumnDef } from '@tanstack/react-table';
import {
  Heading,
  Stack,
  Tag,
  TagText,
  Text,
} from '@alefeducation/ui-elements-library-core';
import styled from 'styled-components';
import { AdminActions } from './AdminActions';
import {
  ACTIONS,
  E_MAIL,
  FIRST_NAME,
  LAST_NAME,
  ORGANISATIONS,
  ROLE,
  STATUS,
  TENANT,
} from '../../utils/constants';
import { AdminUser } from '../../apollo/queries/adminUsers/getAdmins.queries';
import Status from '../Organisations/Status';

const StyledTag = styled(Tag)`
  background-color: ${({ theme }: any) => theme.colors.subjects.english[30]};
  color: ${({ theme }: any) => theme.colors.tag};
`;

const OrganisationTag = styled.div`
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
`;

export interface AdminUsersColumnsOptions {
  refetch: () => void;
  tenantCode: string;
  isViewOnlyRole: boolean;
}

export const getAdminUsersColumns = ({
  refetch,
  tenantCode,
  isViewOnlyRole,
}: AdminUsersColumnsOptions): ColumnDef<AdminUser>[] => {
  const baseColumns: ColumnDef<AdminUser>[] = [
    {
      header: FIRST_NAME,
      accessorKey: 'firstName',
      size: 120,
      cell: (info) => (
        <Stack flexDirection="column" justifyContent="center">
          <Text variant="body-large" color="primary.100" weight="light">
            {info.getValue() as string}
          </Text>
        </Stack>
      ),
    },
    {
      header: LAST_NAME,
      accessorKey: 'lastName',
      size: 120,
      cell: (info) => (
        <Stack flexDirection="column" justifyContent="center">
          <Text variant="body-large" color="primary.100" weight="light">
            {info.getValue() as string}
          </Text>
        </Stack>
      ),
    },
    {
      header: E_MAIL,
      accessorKey: 'email',
      size: 200,
      cell: (info) => (
        <Stack flexDirection="column" justifyContent="center">
          <Text variant="body-large" color="primary.100" weight="light">
            {info.getValue() as string}
          </Text>
        </Stack>
      ),
    },
    {
      header: ROLE,
      accessorKey: 'roles',
      size: 200,
      cell: (info) => {
        const roles = info.getValue() as string[];
        return (
          <Stack
            className="col-roles"
            flexDirection="column"
            justifyContent="center"
          >
            {roles?.map((role, roleIndex) => (
              <StyledTag key={`role-${roleIndex}-${role}`} padding="thick">
                <TagText>{role}</TagText>
              </StyledTag>
            ))}
          </Stack>
        );
      },
    },
    {
      header: TENANT,
      accessorKey: 'tenant',
      size: 80,
      cell: (info) => (
        <Stack flexDirection="column" justifyContent="center">
          <Text variant="body-large" color="primary.100" weight="light">
            {(info.getValue() as string) || ''}
          </Text>
        </Stack>
      ),
    },
    {
      header: ORGANISATIONS,
      id: 'organisations',
      size: 200,
      cell: (info) => {
        const memberships = info.row.original.memberships;
        const organisations = memberships?.map((m) => m.organization) ?? [];
        return (
          <Stack flexDirection="column" justifyContent="center">
            <OrganisationTag>
              {organisations.map(
                (org, orgIndex) =>
                  org && (
                    <StyledTag key={`org-${orgIndex}-${org}`} padding="thick">
                      <TagText>{org}</TagText>
                    </StyledTag>
                  ),
              )}
            </OrganisationTag>
          </Stack>
        );
      },
    },
    {
      header: STATUS,
      accessorKey: 'enabled',
      size: 80,
      cell: (info) => (
        <Stack flexDirection="column" justifyContent="center">
          <Status isActive={info.getValue() as boolean} />
        </Stack>
      ),
    },
  ];

  if (!isViewOnlyRole) {
    baseColumns.push({
      header: ACTIONS,
      id: 'actions',
      size: 66,
      cell: ({ row }) => (
        <AdminActions
          row={row.original}
          tenantCode={tenantCode}
          refetch={refetch}
        />
      ),
    });
  }

  return baseColumns;
};
